import Container from "components/general/Container";
import PageLoader from "components/general/PageLoader";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import SitesHeader from "components/sites/SitesHeader";
import { Box, Button, H1, H3, ParagraphSmall, Text, Typography } from "components/ui";
import Link from "components/ui/Link";
import { useLocalSearchParams } from "expo-router";
import React from "react";
import { Col, Container as GridContainer, Row } from "react-native-flex-grid";
import { useGetSitesByIdSafetyQuery } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";

interface SafetyFile {
	type: string;
	url: string;
}

interface SDSItem {
	stockcode: string;
	url: string;
	name: string;
}

export default function SafetyPage() {
	const { id: siteId } = useLocalSearchParams<{ id: string }>();
	const isMobile = useIsMobile();
	const { data, isLoading, isFetching, isError } = useGetSitesByIdSafetyQuery({
		id: Number(siteId),
	});

	// Process SDS data
	const sdsCollection = React.useMemo<SDSItem[]>(() => {
		if (!data) return [];

		const result: SDSItem[] = [];

		data.forEach((tuple) => {
			try {
				// Skip if any required field is missing
				if (!tuple.item1 || !tuple.item2 || !tuple.item3) return;

				const stockcode = tuple.item1;
				const fileCollection = JSON.parse(tuple.item2) as SafetyFile[];
				const name = tuple.item3;

				fileCollection.forEach((element) => {
					if (element.type === "SDS") {
						result.push({
							stockcode,
							url: element.url,
							name,
						});
					}
				});
			} catch (error) {
				console.error("Error parsing safety data:", error);
			}
		});

		return result;
	}, [data]);

	if (isError) {
		return (
			<ScrollContainer>
				<SitesHeader />
				<Container>
					{isLoading ||
						(isFetching && (
							<Box minHeight={150}>
								<PageLoader />
							</Box>
						))}
					{!isLoading && (
						<Box paddingVertical={24}>
							<H1>Error loading safety data</H1>
							<Text>Please try again later</Text>
						</Box>
					)}
				</Container>
			</ScrollContainer>
		);
	}

	return (
		<ScrollContainer>
			<SitesHeader />
			<Box paddingVertical={24} bg="$neutral25">
				<Container>
					{(isLoading || isFetching) && (
						<Box minHeight={150}>
							<PageLoader />
						</Box>
					)}

					{!isLoading && sdsCollection.length === 0 ? (
						<Section gap={24}>
							<Box flex={2} gap={12}>
								<H3>No SDS available</H3>
								<ParagraphSmall sx={{ lineHeight: 21 }} weight="normal">
									No SDS available for this site.
								</ParagraphSmall>
								<ParagraphSmall sx={{ lineHeight: 21 }} weight="normal">
									Right now, it's empty — once SDS are available, everything will appear here automatically.
								</ParagraphSmall>
							</Box>
						</Section>
					) : (
						<GridContainer
							noPadding
							style={{
								marginLeft: 0,
								maxWidth: "100%",
								marginBottom: 30,
							}}
						>
							{!isMobile && !isLoading && !isFetching && (
								<Row style={{ marginLeft: 0, marginRight: 0, marginBottom: 14, paddingBlock: 16, width: "100%", paddingHorizontal: 16 }}>
									<Col style={{ justifyContent: "center" }} xs={12} md={2}>
										<Text color="$neutral500">Stockcode</Text>
									</Col>
									<Col xs={12} md={4}>
										<Text color="$neutral500">Name</Text>
									</Col>
									<Box marginLeft="auto" paddingHorizontal={12}>
										<Text color="$neutral500">Action</Text>
									</Box>
								</Row>
							)}

							{sdsCollection.map((sds: any, key) => {
								return (
									<Box position="relative" key={sds.stockcode}>
										<Link
											href={sds.url}
											key={sds.stockcode}
											component={(hovered: boolean) => {
												var styleData = {
													marginLeft: 0,
													marginRight: 0,
													paddingHorizontal: 16,
													paddingVertical: 18,
													borderRadius: 8,
													borderWidth: 1,
													marginBottom: 14,
													borderColor: "#E1E9F0",
													backgroundColor: hovered ? "#E1E9F0" : "white",
													width: "100%",
												};

												if (hovered) {
													// @ts-ignore
													styleData = { ...styleData, backgroundColor: "#E1E9F0" };
												}
												if (isMobile) {
													return (
														<Row style={styleData} key={key}>
															<Col></Col>
														</Row>
													);
												}
												return (
													<Row style={styleData} key={key}>
														<Col style={{ justifyContent: "center" }} xs={12} md={2}>
															<Typography weight="bold">{sds.stockcode}</Typography>
														</Col>

														<Col>
															<Text>{sds.name}</Text>
														</Col>
														<Box
															marginLeft="auto"
															paddingHorizontal={12}
															style={{
																display: "flex",
																alignItems: "center",
															}}
														>
															<Typography
																sx={{
																	color: hovered ? "$primary" : "$primarylight",
																	transition: "color 0.2s ease",
																}}
															>
																View
															</Typography>
														</Box>
													</Row>
												);
											}}
										></Link>
									</Box>
								);
							})}
						</GridContainer>
						// <GridContainer
						// 	noPadding
						// 	style={{
						// 		marginLeft: 0,
						// 		width: "100%",
						// 		maxWidth: "100%",
						// 		gap: 16,
						// 		paddingHorizontal: 24,
						// 	}}
						// >
						// 	{/* Only show header on desktop */}
						// 	{!isMobile && <TableHeader />}

						// 	{/* Render SDS items based on device type */}
						// 	{sdsCollection.map((sds, index) => (isMobile ? <MobileSDSItem sds={sds} index={index} key={`sds-${index}`} /> : <DesktopSDSItem sds={sds} index={index} key={`sds-${index}`} />))}
						// </GridContainer>
					)}
				</Container>
			</Box>
		</ScrollContainer>
	);
}
